import React, { CSSProperties, JSX, useEffect, useRef } from "react";
import Head from "next/head";
import Script from "next/script";
import {
  SMARTCOMMERCE_BUTTON_ID,
  SMARTCOMMERCE_CAROUSEL_ID,
  SMARTCOMMERCE_CLIENT_JS,
  SMARTCOMMERCE_CSS,
  SMARTCOMMERCE_ENGINE_JS,
  SMARTCOMMERCE_LOCATOR_ID,
} from "constants/commerce";

interface SmartCartApi {
  loadSmartSiteElements?: (elements: Element[]) => void;
  loadNewWidgets?: () => void;
  checkProductLocatorElements?: () => void;
  smartButtonManager?: { loadSmartButtons: (elements: Element[]) => void };
  carouselManager?: { loadCarousels: (elements: Element[]) => void };
}

interface ProductLocatorUtilities {
  renderProductLocatorElement?: (locatorId: string) => void;
}

interface SmartCommerceWindow extends Window {
  SmartCart?: SmartCartApi;
  SCProductLocatorUtilities?: ProductLocatorUtilities;
}

const initializedLocatorElements = new WeakSet<Element>();

function getSmartCommerceWindow(): SmartCommerceWindow {
  return window as SmartCommerceWindow;
}

function getSmartCart(): SmartCartApi | undefined {
  return getSmartCommerceWindow().SmartCart;
}

function initProductLocators(smartCart?: SmartCartApi): void {
  const locators = Array.from(
    document.querySelectorAll("div[data-sc-product-locator-id]")
  ).filter(
    (element) =>
      element.childElementCount === 0 &&
      !initializedLocatorElements.has(element)
  );
  if (!locators.length) return;

  const utilities = getSmartCommerceWindow().SCProductLocatorUtilities;
  if (typeof utilities?.renderProductLocatorElement === "function") {
    locators.forEach((locator) => {
      const locatorId = locator.getAttribute("data-sc-product-locator-id");
      if (!locatorId) return;
      initializedLocatorElements.add(locator);
      try {
        utilities.renderProductLocatorElement?.(locatorId);
      } catch {
        initializedLocatorElements.delete(locator);
      }
    });
    return;
  }

  if (typeof smartCart?.checkProductLocatorElements === "function") {
    locators.forEach((locator) => initializedLocatorElements.add(locator));
    try {
      smartCart.checkProductLocatorElements();
    } catch {
      locators.forEach((locator) => initializedLocatorElements.delete(locator));
    }
  }
}

export function initSmartCommerce(elements?: Element[]): void {
  if (typeof window === "undefined") return;
  const smartCart = getSmartCart();
  initProductLocators(smartCart);
  if (!smartCart) return;

  const candidates = (
    elements ?? Array.from(document.querySelectorAll("div[data-sc-id]"))
  ).filter((element) => element.hasAttribute("data-sc-id"));
  const pending = candidates.filter(
    (element) => element.childElementCount === 0
  );
  if (!pending.length) return;

  if (typeof smartCart.loadSmartSiteElements === "function") {
    smartCart.loadSmartSiteElements(pending);
    return;
  }

  const buttons = pending.filter(
    (element) => element.getAttribute("data-sc-type") === "SmartButton"
  );
  const carousels = pending.filter(
    (element) =>
      element.hasAttribute("data-sc-id") &&
      element.getAttribute("data-sc-type") !== "SmartButton"
  );
  let initialized = false;
  if (buttons.length && smartCart.smartButtonManager) {
    smartCart.smartButtonManager.loadSmartButtons(buttons);
    initialized = true;
  }
  if (carousels.length && smartCart.carouselManager) {
    smartCart.carouselManager.loadCarousels(carousels);
    initialized = true;
  }
  if (!initialized) smartCart.loadNewWidgets?.();
}

function useSmartCommerceElement(
  accessibleName?: string,
  iframeTitle?: string,
  tabbable = true,
  instanceKey?: string
): React.RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    // SmartCommerce owns every child it injects. A client-side product
    // transition must discard the previous widget before the new UPC scans.
    if (instanceKey !== undefined) element.replaceChildren();

    const enhance = () => {
      if (accessibleName) {
        element
          .querySelectorAll<HTMLButtonElement>("button.sc-btn-ec")
          .forEach((button) => {
            button.setAttribute("aria-label", accessibleName);
            button.tabIndex = tabbable ? 0 : -1;
          });
      }
      if (iframeTitle) {
        element
          .querySelectorAll<HTMLIFrameElement>("iframe")
          .forEach((iframe) => iframe.setAttribute("title", iframeTitle));
      }
    };

    initSmartCommerce([element]);
    enhance();
    const retry = window.setTimeout(() => initSmartCommerce([element]), 80);
    const observer = new MutationObserver(enhance);
    observer.observe(element, { childList: true, subtree: true });

    return () => {
      window.clearTimeout(retry);
      observer.disconnect();
      if (instanceKey !== undefined) element.replaceChildren();
    };
  }, [accessibleName, iframeTitle, tabbable, instanceKey]);

  return ref;
}

export function SmartCommerceAssets(): JSX.Element {
  return (
    <>
      <Head>
        <link rel="stylesheet" href={SMARTCOMMERCE_CSS} key="sc-css" />
      </Head>
      <Script
        src={SMARTCOMMERCE_CLIENT_JS}
        strategy="afterInteractive"
        onReady={initSmartCommerce}
      />
      <Script
        src={SMARTCOMMERCE_ENGINE_JS}
        strategy="afterInteractive"
        onReady={initSmartCommerce}
      />
    </>
  );
}

export function SmartButton({
  upc,
  productName,
  className,
  style,
  tabbable = true,
}: {
  upc: string;
  productName: string;
  className?: string;
  style?: CSSProperties;
  tabbable?: boolean;
}): JSX.Element {
  const ref = useSmartCommerceElement(
    `Add ${productName} to cart`,
    undefined,
    tabbable
  );
  return (
    <div
      ref={ref}
      key={upc}
      className={className}
      style={style}
      data-sc-id={SMARTCOMMERCE_BUTTON_ID}
      data-sc-type="SmartButton"
      data-sc-upc={upc}
      data-sc-product-name={productName}
      data-sc-tabbable={tabbable ? "true" : "false"}
    />
  );
}

export function SmartCommerceCarousel({
  upc,
  className,
}: {
  upc: string;
  className?: string;
}): JSX.Element {
  const ref = useSmartCommerceElement(undefined, undefined, true, upc);
  return (
    <div
      ref={ref}
      key={upc}
      className={className}
      data-sc-id={SMARTCOMMERCE_CAROUSEL_ID}
      data-sc-upc={upc}
      data-sc-render-type="edge"
      data-sc-locale="en-us"
    />
  );
}

export function SmartCommerceLocator({
  className,
}: {
  className?: string;
}): JSX.Element {
  const ref = useSmartCommerceElement(undefined, "Poppi store locator map");
  return (
    <div
      ref={ref}
      className={className}
      style={{ height: "90vh" }}
      data-sc-product-locator-id={SMARTCOMMERCE_LOCATOR_ID}
      data-sc-product-locator-type="standalone"
      data-sc-locale="en-us"
    />
  );
}
